<template>
  <div class="assembly-instructions-media-slider-fi">
    <div class="assembly-instructions-media-slider-fi__slider-controls">
      <div
        v-if="isLG"
        class="assembly-instructions-media-slider-fi__slider-arrows"
      >
        <template v-if="isSlider">
          <button
            type="button"
            :class="{
              'assembly-instructions-media-slider-fi__slider-prev': true,
              'assembly-instructions-media-slider-fi__slider--inactive': isFirstSlide,
            }"
            :aria-disabled="isFirstSlide"
            @click="prevSlide"
          >
            <arrow-icon />
          </button>
          <button
            type="button"
            :class="{
              'assembly-instructions-media-slider-fi__slider-next': true,
              'assembly-instructions-media-slider-fi__slider--inactive': isLastSlide,
            }"
            :aria-disabled="isLastSlide"
            @click="nextSlide"
          >
            <arrow-icon />
          </button>
        </template>
      </div>
      <div
        v-else
        class="assembly-instructions-media-slider-fi__indication"
      >
        <span class="assembly-instructions-media-slider-fi__indication--current">
          {{ visibleCount }}
        </span>
        <span class="assembly-instructions-media-slider-fi__indication--total">
          / {{ mediaItems.length }}
        </span>
      </div>
    </div>

    <carousel-fi
      v-if="mediaItems.length"
      ref="carousel"
      v-intersect.once="onIntersect"
      class="assembly-instructions-media-slider-fi__slider"
      :settings="sliderSettings"
      no-arrows
      @after-change="onAfterChange"
    >
      <carousel-slide-fi
        v-for="(media, index) in mediaItems"
        :key="media.Url ?? index"
        :class="{
          'fi-relative': true,
          'assembly-instructions-media-slider-fi__slide--zoomable': isZoomable,
        }"
        @click="openZoom(index)"
      >
        <span class="assembly-instructions-media-slider-fi__badge">
          {{ index + 1 }}
        </span>
        <cloud-image-fi
          :src="media.Url"
          :alt="media.label"
          :sizes="{
            xxs: { h: 115 },
            xs: { h: 115 },
            sm: { h: 125 },
            md: { h: 142 },
            lg: { h: 142 },
            xl: { h: 142 },
          }"
          params="func=bound"
          do-not-replace-url
        />
        <button
          v-if="isZoomable"
          type="button"
          class="assembly-instructions-media-slider-fi__zoom-button"
          :aria-label="$dict.get('product-Catalog.Zoom')"
          @click.stop="openZoom(index)"
        >
          <icon-fi icon="zoom-in" />
        </button>
      </carousel-slide-fi>
    </carousel-fi>

    <dialog-modal-fi
      v-if="isZoomable"
      ref="zoom"
      class="assembly-instructions-media-slider-fi__zoom-dialog"
      lazy
      full-screen
    >
      <template
        v-if="title"
        #header
      >
        <div class="assembly-instructions-media-slider-fi__zoom-header">
          {{ title }}
        </div>
      </template>
      <template #body>
        <div class="assembly-instructions-media-slider-fi__zoom-body">
          <div class="assembly-instructions-media-slider-fi__zoom-stage">
            <carousel-fi
              ref="carouselZoom"
              :settings="zoomSettings"
              :items="mediaItems"
              theme="catalog-slider"
            >
              <carousel-slide-fi
                v-for="(media, index) in mediaItems"
                :key="media.Url ?? index"
                theme="catalog-slider"
              >
                <cloud-image-fi
                  :src="media.Url"
                  :alt="media.label"
                  :sizes="{
                    sm: { h: 500 },
                    md: { h: 600 },
                    lg: { h: 800 },
                    xl: { h: 900 },
                  }"
                  params="func=bound"
                  do-not-replace-url
                  full-height
                />
              </carousel-slide-fi>
            </carousel-fi>
          </div>

          <div
            v-if="mediaItems.length > 1"
            class="assembly-instructions-media-slider-fi__zoom-thumbnails"
          >
            <carousel-fi
              ref="thumbnailsZoom"
              :settings="zoomThumbnailSettings"
              theme="catalog-slider-thumbnail"
              no-arrows
            >
              <carousel-slide-fi
                v-for="(media, index) in mediaItems"
                :key="media.Url ?? index"
                theme="catalog-slider-thumbnail"
              >
                <cloud-image-fi
                  :src="media.Url"
                  :alt="media.label"
                  :sizes="{
                    sm: { h: 100 },
                    md: { h: 100 },
                    lg: { h: 149 },
                  }"
                  params="func=bound"
                  do-not-replace-url
                />
              </carousel-slide-fi>
            </carousel-fi>
          </div>
        </div>
      </template>
    </dialog-modal-fi>
  </div>
</template>

<script setup lang="ts">
import { ArrowIcon, CarouselFi, CarouselSlideFi, CloudImageFi, DialogModalFi, IconFi } from 'atoms';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

import { useBreakpoints } from '@/composables/breakpoint';
import { useFields } from '@/composables/fields';

defineOptions({ name: 'AssemblyInstructionsMediaSliderFi' });

interface MediaItem {
  Url: string | null;
  label?: string | null;
}

interface Fields {
  enableZoom?: {
    value: boolean;
  };
}

interface Props {
  mediaItems?: MediaItem[];
  title?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  mediaItems: () => [],
  title: null,
});

const { isLG, isMD, windowDimensions } = useBreakpoints();
const fields = useFields<Fields>();

const carousel = useTemplateRef<InstanceType<typeof CarouselFi>>('carousel');
const zoom = useTemplateRef<InstanceType<typeof DialogModalFi>>('zoom');
const carouselZoom = useTemplateRef<InstanceType<typeof CarouselFi>>('carouselZoom');
const thumbnailsZoom = useTemplateRef<InstanceType<typeof CarouselFi>>('thumbnailsZoom');

const isSlider = ref(false);
const isFirstSlide = ref(true);
const isLastSlide = ref(false);
const visibleCount = ref(1);
const visibleItems = ref(1);
const isZoomSynced = ref(false);
const slideGapInPixels = 24;

const sliderSettings = computed(() => ({
  drag: isSlider.value,
  autoWidth: true,
  rewind: false,
  pagination: false,
  perPage: visibleItems.value,
  perMove: 1,
  gap: slideGapInPixels,
}));

const isZoomable = computed(() => isMD.value && Boolean(fields.enableZoom?.value));

const zoomSettings = {
  rewind: true,
  pagination: false,
  type: 'fade',
  gap: '24px',
};

const zoomThumbnailSettings = computed(() => ({
  type: 'slide',
  isNavigation: true,
  perPage: 3,
  perMove: 1,
  autoWidth: true,
  rewind: false,
  pagination: false,
  updateOnMove: true,
  focus: props.mediaItems.length > 4 ? 'center' : 0,
  gap: '24px',
}));

const determineVisibleItems = (sliderWidth: number) => {
  if (!sliderWidth) {
    return;
  }

  const splideSlides = (carousel.value as any).$refs.carousel.splide.Components.Elements.slides;

  let sum = 0;
  splideSlides.every((el: HTMLElement, idx: number) => {
    sum += el.offsetWidth;
    if (idx < splideSlides.length - 1) {
      sum += slideGapInPixels;
    }

    isSlider.value = sum > sliderWidth;
    visibleItems.value = idx;

    return !isSlider.value;
  });

  if (!isLG.value) {
    updateCurrentItemCount();
  }
};

watch(
  windowDimensions,
  () => {
    determineVisibleItems((carousel.value as any)?.$el?.offsetWidth);
  },
  { deep: true },
);

const updateCurrentItemCount = () => {
  const findClosestIndex = (arr: number[], targetValue: number) => {
    return arr.reduce((acc, current, index) => {
      const currentDifference = Math.abs(current - targetValue);
      const minDifference = Math.abs(arr[acc] - targetValue);

      return currentDifference < minDifference ? index : acc;
    }, 0);
  };

  const splideSlides = (carousel.value as any).$refs.carousel.splide.Components.Elements.slides;
  const splideSlideEdgesRight = splideSlides.map((el: Element) => el.getBoundingClientRect().right);
  const sliderEdgeRight = (carousel.value as any).$el.getBoundingClientRect().right;

  visibleCount.value = findClosestIndex(splideSlideEdgesRight, sliderEdgeRight) + 1;
};

const onIntersect = () => {
  determineVisibleItems((carousel.value as any)?.$el?.offsetWidth);
};

const nextSlide = () => {
  if (carousel.value) {
    (carousel.value as any).next();
  }
};

const prevSlide = () => {
  if (carousel.value) {
    (carousel.value as any).prev();
  }
};

const onAfterChange = () => {
  const splide = (carousel.value as any).$refs.carousel.splide;
  isFirstSlide.value = splide?.index === 0;
  isLastSlide.value = splide?.index + visibleItems.value === props.mediaItems.length;
  updateCurrentItemCount();
};

const openZoom = async (index: number) => {
  if (!isZoomable.value) {
    return;
  }

  await zoom.value?.open();
  await nextTick();

  const thumbnailSplide = thumbnailsZoom.value?.getSplide();
  if (thumbnailSplide && !isZoomSynced.value) {
    carouselZoom.value?.sync(thumbnailSplide);
    isZoomSynced.value = true;
  }

  carouselZoom.value?.go(index);
};
</script>

<style scoped src="./assembly-instructions-media-slider-fi" />
